import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Ruler, Tag, ArrowUpRight } from "lucide-react";
import { PROJECTS } from "../data/site";
import { Reveal } from "../components/Reveal";
import CtaBanner from "../components/CtaBanner";
import { useLang } from "../i18n/LanguageContext";

export default function ProjectDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const project = PROJECTS.find((p) => p.id === id);

  if (!project) {
    return <Navigate to="/lavori" replace />;
  }

  const tr = t.projects[project.id];
  const others = PROJECTS.filter((p) => p.id !== id).slice(0, 3);

  return (
    <div data-testid="project-detail-page">
      <div className="pt-24">
        <div className="relative">
          <img src={project.image} alt={tr.title} className="h-[52vh] min-h-[320px] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0">
            <div className="mx-auto max-w-7xl px-5 pb-10 sm:px-8">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">{tr.category}</span>
              <h1 className="mt-2 font-heading text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl" data-testid="project-detail-title">
                {tr.title}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <Link
          to="/lavori"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-brand"
          data-testid="project-detail-back"
        >
          <ArrowLeft size={16} /> {t.projectDetail.back}
        </Link>

        <Reveal className="mt-8 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-slate-900">{t.projectDetail.theProject}</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">{tr.description}</p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">{t.projectDetail.extra}</p>
          </div>
          <div className="rounded-2xl border border-line bg-surface2/60 p-7">
            <div className="space-y-5">
              {[
                { icon: Ruler, label: t.projectDetail.surface, value: project.surface },
                { icon: Tag, label: t.projectDetail.scope, value: t.filters[project.group] },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-3.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                    <Icon size={16} />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-800">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-16">
          <h2 className="font-heading text-xl font-bold text-slate-900">{t.projectDetail.otherProjects}</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-3">
            {others.map((p) => (
              <Link
                key={p.id}
                to={`/lavori/${p.id}`}
                data-testid={`related-project-${p.id}`}
                className="group relative overflow-hidden rounded-2xl border border-line shadow-sm"
              >
                <img src={p.image} alt={t.projects[p.id].title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-400">{t.filters[p.group]}</p>
                    <p className="mt-0.5 font-heading text-sm font-semibold text-white">{t.projects[p.id].title}</p>
                  </div>
                  <ArrowUpRight size={16} className="text-white opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </Link>
            ))}
          </div>
        </Reveal>
      </div>

      <CtaBanner />
    </div>
  );
}
